import type React from 'react';
import { useCallback, useState } from 'react';
import InputError from '../Error';

export interface CheckboxGroupProps
  extends Omit<React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>, 'onChange' | 'value'> {
  label?: string;
  error?: string;
  orientation?: 'horizontal' | 'vertical';
  options: {
    value: string;
    label: string;
    brief?: string;
  }[];
  value?: string[];
  onChange?: (value: string[]) => void;
}

export default function CheckboxGroup({
  label,
  name,
  id,
  options,
  error,
  orientation = 'vertical',
  value: controlledValue,
  onChange,
  ...rest
}: CheckboxGroupProps) {
  // Internal state for uncontrolled mode
  const [internalValue, setInternalValue] = useState<string[]>([]);

  // Determine if controlled or uncontrolled
  const isControlled = controlledValue !== undefined;
  const currentValue = isControlled ? controlledValue : internalValue;

  // Handle checkbox change
  const handleCheckboxChange = useCallback(
    (checkboxValue: string, checked: boolean) => {
      const newValue = checked ? [...currentValue, checkboxValue] : currentValue.filter((v) => v !== checkboxValue);

      if (isControlled) {
        onChange?.(newValue);
      } else {
        setInternalValue(newValue);
        onChange?.(newValue);
      }
    },
    [currentValue, isControlled, onChange]
  );

  // Create Checkbox component function
  const createCheckbox = (props: React.InputHTMLAttributes<HTMLInputElement> & { value: string }) => {
    const { value: checkboxValue, onChange: propOnChange, ...checkboxProps } = props;
    const isChecked = currentValue.includes(checkboxValue);

    return (
      <input
        {...rest}
        {...checkboxProps}
        type='checkbox'
        name={name}
        value={checkboxValue}
        checked={isChecked}
        onChange={(e) => {
          handleCheckboxChange(checkboxValue, e.target.checked);
          propOnChange?.(e);
        }}
      />
    );
  };

  // Checkbox component for render prop compatibility
  const Checkbox = createCheckbox;

  return (
    <>
      <label htmlFor={id} className='block text-sm font-medium text-gray-700'>
        {label}
      </label>
      <div className='space-y-2'>
        <legend className='sr-only'>{label}</legend>
        <div className={orientation === 'horizontal' ? 'flex flex-wrap gap-5' : ''}>
          {options?.map((option, index) => {
            const { value, label: optionLabel, brief } = option;
            const innerId = `${id}-${index}`;
            return (
              <div key={value} className='flex items-center gap-2 py-0.5'>
                <Checkbox id={innerId} type='checkbox' className='h-5 w-5 form-checkbox' value={value} />
                <div className='text-sm'>
                  {optionLabel && (
                    <label htmlFor={innerId} className='font-medium text-gray-700'>
                      {optionLabel}
                    </label>
                  )}
                  {brief && (
                    <p id={`${innerId}-brief`} className='text-gray-500'>
                      {brief}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {error && <InputError>{error}</InputError>}
    </>
  );
}
